import { useState } from 'react';
import { toast } from 'sonner';
import { Pencil, Save, X, KeyRound, Copy, Check, AppWindow, ShieldCheck, AlertTriangle } from 'lucide-react';
import api from '../lib/api';

// Reglages SSO des modules de l'ecosysteme (super_admin).
//
// Deux modes coexistent dans le catalogue :
//  - `legacy`   : le portail ouvre l'app avec `?token=<jeton de session>`
//                 (compta, sirh) ;
//  - `callback` : le portail demande un jeton a usage unique a
//                 /api/sso/generate-token et redirige vers le callback du
//                 module, qui le valide avec sa cle produit (nexus-crm).
// Un module passe en `callback` des qu'il a une URL de callback.
//
// La cle produit n'est jamais relue : l'API ne rend que son prefixe. Elle est
// montree une seule fois, ici, a sa generation.

const STATUS_OPTIONS = [
  { value: 'coming_soon', label: 'Bientot disponible' },
  { value: 'beta', label: 'Beta' },
  { value: 'active', label: 'Actif' },
  { value: 'disabled', label: 'Desactive' },
];

const STATUS_BADGE = {
  active: 'bg-green-100 text-green-700',
  beta: 'bg-indigo-100 text-indigo-700',
  coming_soon: 'bg-yellow-100 text-yellow-700',
  disabled: 'bg-slate-100 text-slate-500',
};

const EMPTY_EDIT = { url: '', sso_callback_url: '', api_base_url: '', status: 'coming_soon' };

const Field = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <label className="block">
    <span className="text-xs font-medium text-slate-500">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:border-nexus-500 focus:ring-1 focus:ring-nexus-500"
    />
  </label>
);

const ModulesSSO = ({ apps, onRefresh }) => {
  const [editingCode, setEditingCode] = useState(null);
  const [editData, setEditData] = useState(EMPTY_EDIT);
  const [saving, setSaving] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(null);
  // { code, key } — la cle en clair, visible tant que l'ecran reste ouvert.
  const [freshKey, setFreshKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const startEdit = (app) => {
    setEditingCode(app.code);
    setEditData({
      url: app.url || '',
      sso_callback_url: app.sso_callback_url || '',
      api_base_url: app.api_base_url || '',
      status: app.status || 'coming_soon',
    });
  };

  const cancelEdit = () => {
    setEditingCode(null);
    setEditData(EMPTY_EDIT);
  };

  const handleSave = async (app) => {
    setSaving(true);
    try {
      const payload = {
        url: editData.url.trim() || null,
        sso_callback_url: editData.sso_callback_url.trim() || null,
        api_base_url: editData.api_base_url.trim() || null,
        status: editData.status,
      };
      await api.put(`/api/apps/${app.code}/sso`, payload);
      toast.success(`Module ${app.name || app.code} mis a jour`);
      cancelEdit();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateKey = async (app) => {
    const confirmed = window.confirm(
      app.api_key_set
        ? `Regenerer la cle de ${app.name || app.code} ? L'ancienne cesse de fonctionner immediatement : le module devra etre reconfigure.`
        : `Generer la cle produit de ${app.name || app.code} ? Elle ne sera affichee qu'une seule fois.`
    );
    if (!confirmed) return;
    setGeneratingCode(app.code);
    try {
      const res = await api.post(`/api/apps/${app.code}/sso/generate-key`);
      setFreshKey({ code: app.code, key: res.data.api_key });
      setCopied(false);
      toast.success('Cle generee — copiez-la maintenant, elle ne sera plus affichee');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la generation de la cle');
    } finally {
      setGeneratingCode(null);
    }
  };

  const copyKey = async () => {
    if (!freshKey) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(freshKey.key);
      } else {
        // Navigateurs sans API presse-papiers : passage par une zone de
        // texte hors ecran et la commande de copie historique.
        const area = document.createElement('textarea');
        area.value = freshKey.key;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.left = '-9999px';
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(area);
        if (!ok) throw new Error('execCommand');
      }
      setCopied(true);
      toast.success('Cle copiee');
    } catch {
      toast.error('Copie impossible : selectionnez la cle et copiez-la a la main');
    }
  };

  if (!apps || apps.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
        <AppWindow className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">Aucun module dans le catalogue</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {freshKey && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-900">
                Cle produit de <span className="font-mono">{freshKey.code}</span> — affichee une seule fois
              </p>
              <p className="text-xs text-amber-800 mt-1">
                A coller dans le <code>.env</code> du module (<code>PRODUCT_API_KEY</code>), puis
                redemarrer son API. Elle n'est pas conservee en clair cote portail.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <code className="flex-1 min-w-0 break-all bg-white border border-amber-200 rounded-lg px-3 py-2 text-xs font-mono text-slate-800 select-all">
                  {freshKey.key}
                </code>
                <button
                  type="button"
                  onClick={copyKey}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 text-white text-xs font-medium hover:bg-amber-700"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copiee' : 'Copier'}
                </button>
                <button
                  type="button"
                  onClick={() => setFreshKey(null)}
                  className="p-2 rounded-lg text-amber-700 hover:bg-amber-100"
                  title="Masquer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">Module</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">SSO</th>
                <th className="px-4 py-3">URLs</th>
                <th className="px-4 py-3">Cle produit</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {apps.map((app) => {
                const editing = editingCode === app.code;
                return (
                  <tr key={app.code} className="align-top hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{app.name || app.code}</p>
                      <p className="text-xs text-slate-400 font-mono">{app.code}</p>
                    </td>
                    <td className="px-4 py-3">
                      {editing ? (
                        <select
                          value={editData.status}
                          onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                          className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                        >
                          {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[app.status] || STATUS_BADGE.disabled}`}>
                          {STATUS_OPTIONS.find((o) => o.value === app.status)?.label || app.status}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {app.sso_mode === 'callback' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <ShieldCheck className="w-3 h-3" /> callback
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                          legacy
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 min-w-[20rem]">
                      {editing ? (
                        <div className="space-y-2">
                          <Field label="Site du module" value={editData.url} placeholder="https://crm.nexus-360.fr"
                            onChange={(v) => setEditData({ ...editData, url: v })} />
                          <Field label="URL de callback SSO (servie par l'API du module)" value={editData.sso_callback_url}
                            placeholder="https://api-crm.nexus-360.fr/sso/callback"
                            onChange={(v) => setEditData({ ...editData, sso_callback_url: v })} />
                          <Field label="Base de l'API" value={editData.api_base_url} placeholder="https://api-crm.nexus-360.fr"
                            onChange={(v) => setEditData({ ...editData, api_base_url: v })} />
                          <p className="text-xs text-slate-400">
                            Un module « Beta » ou « Actif » doit avoir les trois URLs.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-0.5 text-xs">
                          <p className="text-slate-700 break-all">{app.url || <span className="text-slate-400">— site —</span>}</p>
                          <p className="text-slate-500 break-all">{app.sso_callback_url || <span className="text-slate-400">— callback —</span>}</p>
                          <p className="text-slate-500 break-all">{app.api_base_url || <span className="text-slate-400">— API —</span>}</p>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {app.api_key_set ? (
                        <p className="text-xs font-mono text-slate-600" title="Prefixe de la cle en service">
                          {app.api_key_prefix}…
                        </p>
                      ) : (
                        <p className="text-xs text-slate-400">Aucune</p>
                      )}
                      <button
                        type="button"
                        onClick={() => handleGenerateKey(app)}
                        disabled={generatingCode === app.code}
                        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-nexus-600 hover:text-nexus-700 disabled:opacity-50"
                      >
                        <KeyRound className="w-3.5 h-3.5" />
                        {app.api_key_set ? 'Regenerer' : 'Generer la cle'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {editing ? (
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleSave(app)}
                            disabled={saving}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-nexus-600 text-white text-xs font-medium hover:bg-nexus-700 disabled:opacity-50"
                          >
                            <Save className="w-3.5 h-3.5" /> Enregistrer
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
                            title="Annuler"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startEdit(app)}
                          className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-nexus-600"
                          title="Modifier"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ModulesSSO;
